import React from "react";
import Box from "@mui/material/Box";
import Paper from "@mui/material/Paper";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import TextField from "@mui/material/TextField";
import InputAdornment from "@mui/material/InputAdornment";
import Button from "@mui/material/Button";
import SearchIcon from "@mui/icons-material/Search";

/**
 * Encabezado unificado para las pantallas de listado/consulta de Servicios.
 * Mismo lenguaje visual que Configuración → Archivos (MaestroArchivo):
 * tarjeta con badge de ícono + título + "N registros" + buscador + botones.
 *
 * Props:
 *  - icono:     ReactNode (ícono MUI)
 *  - titulo:    string
 *  - count:     number | null   -> "N registros" (si null, "—")
 *  - onBuscar:  (valor) => void  -> se conecta al globalFilter del DataTable
 *  - buscarPlaceholder?: string
 *  - acciones?: Array<{ label, icon?, color?, variant?, onClick, disabled? }>
 *  - extra?:    ReactNode  -> control libre (toggle, filtro) junto al buscador
 */
export default function PantallaHeader({
  icono,
  titulo,
  count = null,
  onBuscar,
  buscarPlaceholder = "Buscar…",
  acciones = [],
  extra = null,
}) {
  return (
    <Paper
      elevation={0}
      sx={{
        p: 2,
        mb: 2,
        border: "1px solid",
        borderColor: "divider",
        borderRadius: 2,
      }}
    >
      <Stack
        direction={{ xs: "column", md: "row" }}
        spacing={2}
        alignItems={{ xs: "stretch", md: "center" }}
      >
        <Stack
          direction="row"
          spacing={1.5}
          alignItems="center"
          sx={{ flexShrink: 0 }}
        >
          <Box
            sx={{
              width: 40,
              height: 40,
              borderRadius: 2,
              display: "grid",
              placeItems: "center",
              bgcolor: "primary.main",
              color: "primary.contrastText",
            }}
          >
            {icono}
          </Box>
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 700, lineHeight: 1.2 }}>
              {titulo}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {count != null ? `${count} registros` : "—"}
            </Typography>
          </Box>
        </Stack>

        <Box sx={{ flexGrow: 1 }} />

        {extra}

        {onBuscar ? (
          <TextField
            size="small"
            placeholder={buscarPlaceholder}
            onInput={(e) => onBuscar(e.target.value)}
            sx={{ minWidth: { xs: "100%", md: 240 } }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
            }}
          />
        ) : null}
      </Stack>

      {acciones.length ? (
        <Stack
          direction="row"
          spacing={1}
          flexWrap="wrap"
          useFlexGap
          alignItems="center"
          sx={{ mt: 2 }}
        >
          {acciones.map((a, i) => (
            <Button
              key={a.label || i}
              variant={a.variant || "outlined"}
              color={a.color || "primary"}
              startIcon={a.icon}
              onClick={a.onClick}
              disabled={a.disabled}
            >
              {a.label}
            </Button>
          ))}
        </Stack>
      ) : null}
    </Paper>
  );
}
